import {
  FormikCheckbox,
  FormikDatepicker,
  FormikInput,
  FormikRadio,
  FormikSelect,
  FormikTextArea,
} from 'components/Formik';
import { CUSTOM_FIELD_DATA_TYPES } from 'global/options';
import { ICustomFieldItem } from 'hooks/customField/useFetchPaginatedCustomFields';

interface ICustomFieldBuilderProps {
  customField: ICustomFieldItem;
  shouldValidateField?: boolean;
}

const CustomFieldBuilder = ({ customField, shouldValidateField = true }: ICustomFieldBuilderProps) => {
  const validateField = (value: any) => {
    if (!shouldValidateField) return null;

    if (customField.is_required && ((typeof value === 'string' && value?.trim() === '') || !value)) {
      return { msg: 'This is required' };
    }

    // Dropdown Multiselect Validation
    if (
      customField.is_required &&
      (customField.field_type === CUSTOM_FIELD_DATA_TYPES.DROPDOWN_MULTISELECT ||
        customField.field_type === CUSTOM_FIELD_DATA_TYPES.CHECKBOX) &&
      (!value || !Array.isArray(value) || !value?.length)
    ) {
      return { msg: 'This is required' };
    }

    // text and textarea input validation
    if (
      value &&
      (customField.field_type === CUSTOM_FIELD_DATA_TYPES.TEXT ||
        customField.field_type === CUSTOM_FIELD_DATA_TYPES.TEXTAREA)
    ) {
      if (customField.min_length && value.length < customField.min_length) {
        return { msg: `${customField.field_name} must be at least ${customField.min_length} characters.` };
      }

      if (customField.max_length && value.length > customField.max_length) {
        return { msg: `${customField.field_name} must be less than ${customField.max_length} characters.` };
      }
    }

    // Number input validation
    if (
      value &&
      (customField.field_type === CUSTOM_FIELD_DATA_TYPES.NUMERIC ||
        customField.field_type === CUSTOM_FIELD_DATA_TYPES.MONETARY)
    ) {
      if (isNaN(value)) {
        return { msg: `${customField.field_name} must be a valid number.` };
      }
      if (customField.min_value !== null && value < customField.min_value) {
        return { msg: `${customField.field_name} must be at least ${customField.min_value}.` };
      }
      if (customField.max_value !== null && value > customField.max_value) {
        return { msg: `${customField.field_name} must be less than or equal to ${customField.max_value}.` };
      }
    }
    return null;
  };

  return (
    <>
      {customField.field_type === CUSTOM_FIELD_DATA_TYPES.TEXT ||
      customField.field_type === CUSTOM_FIELD_DATA_TYPES.NUMERIC ||
      customField.field_type === CUSTOM_FIELD_DATA_TYPES.TIME_PICKER ||
      customField.field_type === CUSTOM_FIELD_DATA_TYPES.MONETARY ? (
        <FormikInput
          type={customField.field_type === CUSTOM_FIELD_DATA_TYPES.TIME_PICKER ? 'time' : 'text'}
          name={customField.id}
          label={customField.field_name}
          inputProps={{
            placeholder: customField.placeholder,
          }}
          formControlProps={{
            isRequired: shouldValidateField && customField.is_required,
          }}
          fieldProps={{
            validate: (value: any) => validateField(value),
          }}
        />
      ) : null}

      {customField.field_type === CUSTOM_FIELD_DATA_TYPES.TEXTAREA ? (
        <FormikTextArea
          name={customField.id}
          label={customField.field_name}
          textareaProps={{
            placeholder: customField.placeholder,
          }}
          formControlProps={{
            isRequired: shouldValidateField && customField.is_required,
          }}
          fieldProps={{
            validate: (value: any) => validateField(value),
          }}
        />
      ) : null}

      {customField.field_type === CUSTOM_FIELD_DATA_TYPES.DROPDOWN ||
      customField.field_type === CUSTOM_FIELD_DATA_TYPES.DROPDOWN_MULTISELECT ? (
        <FormikSelect
          name={customField.id}
          label={customField.field_name}
          options={customField.field_options?.map((option: string) => ({ label: option, value: option })) || []}
          selectProps={{
            placeholder: customField.placeholder,
            isMulti: customField.field_type === CUSTOM_FIELD_DATA_TYPES.DROPDOWN_MULTISELECT,
          }}
          formControlProps={{
            isRequired: shouldValidateField && customField.is_required,
          }}
          fieldProps={{
            validate: (value: any) => validateField(value),
          }}
        />
      ) : null}

      {customField.field_type === CUSTOM_FIELD_DATA_TYPES.CHECKBOX ? (
        <FormikCheckbox
          name={customField.id}
          label={customField.field_name}
          options={customField.field_options?.map((option: string) => ({ label: option, value: option })) || []}
          formControlProps={{
            isRequired: shouldValidateField && customField.is_required,
          }}
          fieldProps={{
            validate: (value: any) => validateField(value),
          }}
        />
      ) : null}

      {customField.field_type === CUSTOM_FIELD_DATA_TYPES.RADIO_BUTTON ? (
        <FormikRadio
          name={customField.id}
          label={customField.field_name}
          options={customField.field_options?.map((option: string) => ({ label: option, value: option })) || []}
          formControlProps={{
            isRequired: shouldValidateField && customField.is_required,
          }}
          fieldProps={{
            validate: (value: any) => validateField(value),
          }}
        />
      ) : null}

      {customField.field_type === CUSTOM_FIELD_DATA_TYPES.DATE_PICKER ? (
        <FormikDatepicker
          label={customField.field_name}
          name={customField.id}
          formControlProps={{
            isRequired: shouldValidateField && customField.is_required,
          }}
          fieldProps={{
            validate: (value: any) => validateField(value),
          }}
        />
      ) : null}
    </>
  );
};

export default CustomFieldBuilder;
